import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { PrescriptionRegistration } from '@/entities/business-relationship/api/businessRelationship.api';
import { usePrescriptionRegistrations } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { Card, CardHeader } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatShortYm, toUrlYm } from '@/shared/utils/format';

/** 기본으로 펼치는 등록월 수 (M-04) */
const COLLAPSED_MONTHS = 2;
/** 연도 선택 — 승인일을 모를 때 보여 줄 최소 연도 수 */
const MIN_YEARS = 2;

const StepDivider = () => (
  <span className="shrink-0 text-[13px] leading-5 text-[#d5d5d5]">›</span>
);

/** 처방확인 · 정산확정은 완료 ✓ / 미완료 ○ 아이콘으로만 표시한다 */
const DoneStep = ({ done, label }: { done: boolean; label: string }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center gap-[5px] text-center">
    <span
      className={cn(
        'text-[17px] leading-[26px] font-bold',
        done ? 'text-success' : 'text-[#c4c4c4]',
      )}
      aria-label={done ? '완료' : '미완료'}
    >
      {done ? '✓' : '○'}
    </span>
    <span className="text-[11px] leading-[17px] text-ink-6">{label}</span>
  </div>
);

interface HistoryRow {
  key: string;
  /** 등록월 yyyyMM */
  ym: string;
  month: number;
  /** 없으면 그 달은 미등록 빈 행 */
  item?: PrescriptionRegistration;
}

const yearOf = (date?: string | null) =>
  date ? Number(date.slice(0, 4)) : undefined;

/**
 * M-04 처방 내역 카드 — 등록월별 처방등록 장수 · 처방확인 · 정산확정 진행 상태.
 * GET /prescription-registrations?registrationYear= 는 이미지가 있는 (등록월, 정산월)만 주므로
 * 빈 달은 미등록 행으로 채운다. 같은 등록월에 정산월이 여러 개면 행도 여러 개다 (합치지 않음).
 *
 * @param currentRegistrationYm 오늘 올리면 들어갈 등록월 — 올해는 이 달까지 보여준다
 * @param latestApprovalDate 연도 선택 범위의 시작 (목록 카드 값)
 * @param endedAt 종료 · 거절 거래처면 종료일 — 기본 연도를 그 해로 둔다
 */
export const PrescriptionHistoryCard = ({
  relationshipId,
  clinicId,
  currentRegistrationYm,
  latestApprovalDate,
  endedAt,
}: {
  relationshipId: number;
  clinicId: number;
  currentRegistrationYm: string;
  latestApprovalDate: string | null;
  endedAt: string | null;
}) => {
  const currentYear = Number(currentRegistrationYm.slice(0, 4));
  const currentMonth = Number(currentRegistrationYm.slice(4, 6));

  const years = useMemo(() => {
    const first = Math.min(
      yearOf(latestApprovalDate) ?? currentYear - (MIN_YEARS - 1),
      yearOf(endedAt) ?? currentYear,
      currentYear - (MIN_YEARS - 1),
    );
    return Array.from(
      { length: currentYear - first + 1 },
      (_, i) => currentYear - i,
    );
  }, [currentYear, latestApprovalDate, endedAt]);

  const [year, setYear] = useState(() => yearOf(endedAt) ?? currentYear);
  const [expanded, setExpanded] = useState(false);
  const registrations = usePrescriptionRegistrations(relationshipId, year);
  // 연도를 바꾸는 동안에는 이전 연도 응답이 placeholder로 남는다
  const items = useMemo(
    () =>
      registrations.data?.registrationYear === year
        ? registrations.data.items
        : [],
    [registrations.data, year],
  );

  const rows = useMemo(() => {
    const lastMonth = year === currentYear ? currentMonth : 12;
    return Array.from({ length: lastMonth }, (_, i) => {
      const month = lastMonth - i;
      const ym = `${year}${String(month).padStart(2, '0')}`;
      const matches = items.filter((r) => r.registrationYearMonth === ym);
      return matches.length === 0
        ? [{ key: ym, ym, month }]
        : matches.map((item): HistoryRow => ({
            key: `${ym}-${item.settlementYearMonth}`,
            ym,
            month,
            item,
          }));
    });
  }, [items, year, currentYear, currentMonth]);

  // M-09 월 실적 — 등록 내역이 있는 가장 최근 정산월 · 이 병의원으로 연다 (없으면 연간 실적)
  const latestSettlementYm = items[0]?.settlementYearMonth;
  const hasAny = items.length > 0;
  const shownRows = (expanded ? rows : rows.slice(0, COLLAPSED_MONTHS)).flat();
  const loading = registrations.isPending || registrations.isPlaceholderData;

  return (
    <Card>
      <CardHeader
        title="처방 내역"
        description="등록월별 처방 등록 · 처방 확인 · 정산 확정 진행 상태입니다."
        actions={
          <>
            <label className="relative">
              <span className="sr-only">연도 선택</span>
              <select
                value={year}
                onChange={(e) => {
                  setYear(Number(e.target.value));
                  setExpanded(false);
                }}
                className="h-[38px] appearance-none rounded-[9px] border border-line-strong bg-surface pr-8 pl-3.5 text-[13px] leading-5 font-medium text-ink outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}년
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-ink-6" />
            </label>
            <Link
              {...(latestSettlementYm
                ? {
                    to: ROUTE_PATHS.PERFORMANCE_MONTH,
                    params: { yearMonth: toUrlYm(latestSettlementYm) },
                    search: { clinicId },
                  }
                : { to: ROUTE_PATHS.PERFORMANCE })}
              className="ml-auto inline-flex items-center text-xs leading-[18px] font-medium text-primary hover:underline lg:ml-0"
            >
              전체 내역 보기
              <ChevronRight className="size-3.5" />
            </Link>
          </>
        }
      />

      {registrations.isError ? (
        <ErrorState onRetry={() => void registrations.refetch()} />
      ) : loading ? (
        <div className="mt-[18px] flex flex-col gap-2.5">
          {Array.from({ length: COLLAPSED_MONTHS }, (_, i) => (
            <Skeleton key={i} className="h-[78px] rounded-lg" />
          ))}
        </div>
      ) : !hasAny ? (
        <EmptyState message="등록된 처방 내역이 없어요." className="pb-2" />
      ) : (
        <>
          <ul className="mt-[18px] flex flex-col gap-2.5">
            {shownRows.map(({ key, ym, month, item }) => {
              const count = item?.imageCount ?? 0;
              const empty = count === 0;
              // 지금 등록 중인 달은 비어 있어도 흐리게 하지 않는다 (Figma 7월 행)
              const dimmed = empty && ym < currentRegistrationYm;
              return (
                <li
                  key={key}
                  className={cn(
                    'flex items-center rounded-lg border border-line-soft bg-subtle py-3 pr-3.5 pl-4 lg:pl-5',
                    dimmed && 'opacity-55',
                  )}
                >
                  <div className="flex w-10 shrink-0 flex-col lg:w-14">
                    <p className="text-[15px] leading-[23px] font-bold text-ink">
                      {month}월
                    </p>
                    {item && (
                      <p className="text-[10px] leading-[15px] text-ink-6">
                        {formatShortYm(item.settlementYearMonth)} 정산
                      </p>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 items-center">
                    <div className="flex min-w-0 flex-1 flex-col items-center gap-[5px] text-center">
                      <span
                        className={cn(
                          'text-[19px] leading-[29px] font-bold',
                          empty ? 'text-[#c4c4c4]' : 'text-primary',
                        )}
                      >
                        {empty ? '—' : `${count}장`}
                      </span>
                      <span className="text-[11px] leading-[17px] text-ink-6">
                        처방등록
                      </span>
                    </div>
                    <StepDivider />
                    {/* 그 행 이미지 중 하나라도 확인 완료면 처방확인 ✓ */}
                    <DoneStep
                      done={(item?.verifiedImageCount ?? 0) > 0}
                      label="처방확인"
                    />
                    <StepDivider />
                    <DoneStep
                      done={!!item?.isSettlementConfirmed}
                      label="정산확정"
                    />
                  </div>
                  {item ? (
                    <Button
                      variant="secondary"
                      className="h-[34px] w-[52px] rounded-sm lg:w-[60px]"
                      asChild
                    >
                      <Link
                        to={ROUTE_PATHS.PERFORMANCE_MONTH}
                        params={{
                          yearMonth: toUrlYm(item.settlementYearMonth),
                        }}
                        search={{ clinicId }}
                      >
                        상세
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      className="h-[34px] w-[52px] rounded-sm lg:w-[60px]"
                      disabled
                    >
                      상세
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {rows.length > COLLAPSED_MONTHS && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 flex h-[38px] w-full items-center justify-center gap-1.5 rounded-[9px] bg-field text-xs leading-[18px] font-medium text-ink-3 hover:bg-muted lg:w-[400px]"
            >
              {expanded ? '접기' : '전체 보기'}
              {expanded ? (
                <ChevronUp className="size-3.5 text-ink-6" />
              ) : (
                <ChevronDown className="size-3.5 text-ink-6" />
              )}
            </button>
          )}
        </>
      )}
    </Card>
  );
};
